import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';

import { SignedDocumentsTable } from '@/components/tables/SignedDocumentsTable';
import { ZeeDocumentsTable } from '@/components/tables/ZeeDocumentsTable';
import { ZorDocumentsTable } from '@/components/tables/ZorDocumentsTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SIGNED_DUMMY_DOCUMENTS } from '@/constants/documents/dummy';
import { LIBRARY_TOUR_CLASSES } from '@/constants/tour/classNames';
import { toast } from '@/hooks/ui/use-toast';
import { useGetAllZeeDocuments } from '@/hooks/zee-document/useGetAllZeeDocuments';
import { useGetAllZorDocuments } from '@/hooks/zor-document/useGetAllZorDocuments';
import { useZeeDocumentStore } from '@/stores/useZeeDocumentStore';
import { useZorDocumentStore } from '@/stores/useZorDocumentStore';

const DocumentTab = () => {
  const { zorDocuments, setZorDocuments } = useZorDocumentStore();
  const { zeeDocuments, setZeeDocuments } = useZeeDocumentStore();

  const {
    result: zorDocumentsResult,
    isError: zorDocumentsError,
    isSuccess: zorDocumentsSuccess,
    isLoading: zorDocumentsLoading,
    isRefetching: zorDocumentsRefetching,
  } = useGetAllZorDocuments();

  const {
    result: zeeDocumentsResult,
    isError: zeeDocumentsError,
    isSuccess: zeeDocumentsSuccess,
    isLoading: zeeDocumentsLoading,
    isRefetching: zeeDocumentsRefetching,
  } = useGetAllZeeDocuments();

  useEffect(() => {
    if (zorDocumentsError || zeeDocumentsError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  }, [zorDocumentsError, zeeDocumentsError]);

  useEffect(() => {
    if (zorDocumentsSuccess && zorDocumentsResult?.data) {
      setZorDocuments(zorDocumentsResult.data);
    }
  }, [zorDocumentsSuccess, zorDocumentsResult, setZorDocuments]);

  useEffect(() => {
    if (zeeDocumentsSuccess && zeeDocumentsResult?.data) {
      setZeeDocuments(zeeDocumentsResult.data);
    }
  }, [zeeDocumentsSuccess, zeeDocumentsResult, setZeeDocuments]);

  if (zorDocumentsLoading || zeeDocumentsLoading) {
    return (
      <div className="flex h-full w-full flex-1 items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-2 border-b border-border pb-2 font-medium text-zinc-700">
        Documents
        {(zorDocumentsRefetching || zeeDocumentsRefetching) && (
          <Loader2 className="size-4 animate-spin" />
        )}
      </div>
      <div className="rounded-xl md:bg-white md:p-4">
        <Tabs defaultValue="shared-docs">
          <div className="flex items-center justify-between">
            <TabsList
              className={`${LIBRARY_TOUR_CLASSES.DOCUMENTS_SUB_TABS} w-full max-md:bg-slate-200 md:w-fit`}
            >
              <TabsTrigger
                value="shared-docs"
                className={`${LIBRARY_TOUR_CLASSES.SHARED_DOCS_TAB} w-full`}
              >
                Shared Docs
              </TabsTrigger>
              {/*<TabsTrigger*/}
              {/*  value="signed-docs"*/}
              {/*  className={`${LIBRARY_TOUR_CLASSES.SIGNED_DOCS_TAB} w-full`}*/}
              {/*>*/}
              {/*  Signed Docs*/}
              {/*</TabsTrigger>*/}
              <TabsTrigger
                value="uploaded-docs"
                className={`${LIBRARY_TOUR_CLASSES.UPLOADED_DOCS_TAB} w-full`}
              >
                Uploaded Docs
              </TabsTrigger>
            </TabsList>
          </div>
          {zorDocumentsLoading || zeeDocumentsLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="size-4 animate-spin" />
            </div>
          ) : (
            <>
              <TabsContent
                value="shared-docs"
                className={LIBRARY_TOUR_CLASSES.DOCUMENTS_TABLE}
              >
                <ZorDocumentsTable data={zorDocuments || []} />
              </TabsContent>
              <TabsContent
                value="signed-docs"
                className={LIBRARY_TOUR_CLASSES.DOCUMENTS_TABLE}
              >
                <SignedDocumentsTable data={SIGNED_DUMMY_DOCUMENTS} />
              </TabsContent>
              <TabsContent
                value="uploaded-docs"
                className={LIBRARY_TOUR_CLASSES.DOCUMENTS_TABLE}
              >
                <ZeeDocumentsTable data={zeeDocuments || []} />
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export { DocumentTab };
